import { computed, useHost, onBeforeUnmount, type ComputedRef, type Ref, type ToRefs } from "vue";
import { useSharedState } from "composables/useSharedState";
import { useInheritProps } from "composables/useInheritProps";
import { selectionHelpers } from "composables/useCollectionItem";
import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import { getFieldStyles, useFieldInheritance } from "components/Field/useField";
import { useDropdownBase, type ModelValueType } from "composables/useDropdownBase";
import type { SelectProps } from "./select.types";
import { selectClasses } from "./select.styles";
import type { AcceptableValue } from "@/runtime/types/utils";
import type { Colors, Variants, Sizes } from "types";

const SELECT_TYPE = "Select";
const SELECT_ITEM_TYPE = "SelectItem";

/**
 * Registry mapping item values to their display labels.
 * Used for showing the selected item's label in the trigger.
 */
const itemLabelRegistry = new WeakMap<Element, Map<AcceptableValue, string>>();

/**
 * Registers a Select element's label registry.
 */
export const registerSelect = (element: Element): void => {
  itemLabelRegistry.set(element, new Map());
};

/**
 * Unregisters a Select element's label registry.
 */
export const unregisterSelect = (element: Element): void => {
  itemLabelRegistry.delete(element);
};

/**
 * Registers an item's value-to-label mapping.
 */
export const registerSelectItem = (selectElement: Element, value: AcceptableValue, label: string): void => {
  const labelMap = itemLabelRegistry.get(selectElement);
  if (labelMap) {
    labelMap.set(value, label);
  }
};

/**
 * Unregisters an item's value-to-label mapping.
 */
export const unregisterSelectItem = (selectElement: Element, value: AcceptableValue): void => {
  const labelMap = itemLabelRegistry.get(selectElement);
  if (labelMap) {
    labelMap.delete(value);
  }
};

/**
 * Gets the label for a given value from the registry.
 */
export const getItemLabel = (selectElement: Element, value: AcceptableValue): string | undefined => {
  const labelMap = itemLabelRegistry.get(selectElement);
  return labelMap?.get(value);
};

export interface UseSelectOptions {
  /**
   * The value of the current item. Pass this from SelectItem
   * to enable isSelected computation without relying on state store lookup.
   */
  itemValue?: Ref<AcceptableValue | undefined>;
}

export interface UseSelectReturn {
  // Select context
  selectUid: ComputedRef<number | undefined>;
  selectProps: ComputedRef<SelectProps | undefined>;
  selectElement: ComputedRef<Element | null>;

  // Item context
  isSelected: ComputedRef<boolean>;
  isHighlighted: ComputedRef<boolean>;
  isDisabled: ComputedRef<boolean>;

  // Open state
  isOpen: ComputedRef<boolean>;
  open: () => void;
  close: () => void;
  toggle: () => void;

  // Selection
  select: (value?: AcceptableValue) => void;
  deselect: (value?: AcceptableValue) => void;
  toggleSelection: (value?: AcceptableValue) => void;
  setModelValue: (value: ModelValueType) => void;

  // Highlight (for keyboard navigation)
  highlightedValue: ComputedRef<AcceptableValue | undefined>;
  setHighlightedValue: (value: AcceptableValue | undefined) => void;

  // Focus (triggers item self-focus)
  focusValue: ComputedRef<AcceptableValue | undefined>;
  setFocusValue: (value: AcceptableValue | undefined) => void;
  shouldFocus: ComputedRef<boolean>;

  // Value
  modelValue: ComputedRef<AcceptableValue | AcceptableValue[] | undefined>;
  displayValue: ComputedRef<string>;

  // Styling (inherits from Field when available)
  finalColor: ComputedRef<Colors>;
  finalVariant: ComputedRef<Exclude<Variants, "link" | "ghost">>;
  finalSize: ComputedRef<Sizes>;
}

/** Build selection actions that delegate to the dropdown base. */
const buildSelectActions = (base: ReturnType<typeof useDropdownBase>) => {
  const close = () => base.setProp("open", false);
  const toggle = () => base.setProp("open", !base.isOpen.value);

  const select = (value?: AcceptableValue) => {
    const itemValue = base.resolveItemValue(value);
    if (itemValue === undefined || base.parentUid.value === undefined) return;
    const props = base.parentProps.value;
    if (!props || props.disabled) return;
    base.setProp("modelValue", selectionHelpers.select(itemValue, props.modelValue, props.multiple ?? false));
    if (!props.multiple) close();
  };

  const deselect = (value?: AcceptableValue) => {
    const itemValue = base.resolveItemValue(value);
    if (itemValue === undefined || base.parentUid.value === undefined) return;
    const props = base.parentProps.value;
    if (!props || props.disabled) return;
    base.setProp("modelValue", selectionHelpers.deselect(itemValue, props.modelValue, props.multiple ?? false));
  };

  const toggleSelection = (value?: AcceptableValue) => {
    const itemValue = base.resolveItemValue(value);
    if (itemValue === undefined) return;
    const props = base.parentProps.value;
    if (!props || props.disabled) return;
    const behavior = props.multiple ? "toggle" : "replace";
    base.setProp("modelValue", selectionHelpers.toggle(itemValue, props.modelValue, props.multiple ?? false, behavior));
    if (!props.multiple) close();
  };

  const setModelValue = (value: ModelValueType) => {
    if (base.parentUid.value === undefined) return;
    const props = base.parentProps.value;
    if (!props || props.disabled) return;
    base.setProp("modelValue", value);
  };

  return { close, toggle, select, deselect, toggleSelection, setModelValue };
};

/** Set up field inheritance (color/variant/size from parent e-field). */
const buildFieldInheritance = (base: ReturnType<typeof useDropdownBase>) => {
  const selectFieldElement = computed(() => {
    const el = base.parentElement.value;
    return el ? (closestAcrossShadowBoundaries(el, "e-field") ?? undefined) : undefined;
  });

  const selectStateForInheritance = computed(() => {
    const props = base.parentProps.value as SelectProps | undefined;
    if (!props) return {};
    return { color: { value: props.color }, variant: { value: props.variant }, size: { value: props.size } };
  });

  return useInheritProps({
    parentState: computed(() => {
      const el = selectFieldElement.value;
      return el ? getFieldStyles(el) : undefined;
    }),
    ownState: selectStateForInheritance as any,
    inherit: { color: { default: "neutral" }, variant: { default: "subtle" }, size: { default: "md" } },
    shouldInherit: (parent) => parent?.finalColor?.value === "error",
  });
};

export const useSelect = (options?: UseSelectOptions): UseSelectReturn => {
  const base = useDropdownBase(
    {
      parentType: SELECT_TYPE,
      itemType: SELECT_ITEM_TYPE,
      getItemLabel,
    },
    options,
  );

  const actions = buildSelectActions(base);
  const { finalColor, finalVariant, finalSize } = buildFieldInheritance(base);

  return {
    selectUid: base.parentUid,
    selectProps: base.parentProps as ComputedRef<SelectProps | undefined>,
    selectElement: base.parentElement,
    isSelected: base.isSelected,
    isHighlighted: base.isHighlighted,
    isDisabled: base.isDisabled,
    isOpen: base.isOpen,
    open: base.open,
    ...actions,
    highlightedValue: base.highlightedValue,
    setHighlightedValue: base.setHighlightedValue,
    focusValue: base.focusValue,
    setFocusValue: base.setFocusValue,
    shouldFocus: base.shouldFocus,
    modelValue: base.modelValue,
    displayValue: base.displayValue,
    finalColor,
    finalVariant,
    finalSize,
  };
};

/* -------------------------------------------------------------------------- */
/*  useSelectSetup - Main Select component composable                         */
/* -------------------------------------------------------------------------- */

export interface UseSelectSetupOptions {
  /** Props passed directly from the component */
  props: Readonly<SelectProps>;
}

export interface UseSelectSetupReturn {
  /** Reactive state from props */
  state: ToRefs<SelectProps>;

  /** Final color - inherits from Field when available */
  finalColor: ComputedRef<Colors>;

  /** Final variant - inherits from Field when available */
  finalVariant: ComputedRef<Exclude<Variants, "link" | "ghost">>;

  /** Final size - inherits from Field when available */
  finalSize: ComputedRef<Sizes>;

  /** Root variant classes for the Select host */
  classes: ComputedRef<unknown>;
}

/**
 * Composable for the main Select component setup.
 * Handles registration and lifecycle.
 *
 * Called from Select.vue with props - NOT state.
 * Calls useSharedState internally (in the composable, not the component).
 */
export const useSelectSetup = (options: UseSelectSetupOptions): UseSelectSetupReturn => {
  const { props } = options;
  const host = useHost();

  // Call useSharedState here (in the composable, not in the component)
  const state = useSharedState(props);

  // Register label registry for this Select
  if (host) {
    registerSelect(host);
  }

  onBeforeUnmount(() => {
    if (host) {
      unregisterSelect(host);
    }
  });

  // Inherit color, variant, size from parent Field (only when Field has error)
  const { finalColor, finalVariant, finalSize } = useFieldInheritance(host, state);

  /**
   * Focus the select trigger - attached to host for Field label clicks
   */
  const focus = () => {
    if (host) {
      const trigger = host.querySelector('[role="combobox"]') as HTMLElement | null;
      trigger?.focus();
    }
  };

  // Attach focus method to the custom element host for DOM access
  if (host) {
    (host as any).focus = focus;
  }

  const classes = computed(() =>
    selectClasses({
      disabled: state.disabled?.value,
      block: state.block?.value,
    }),
  );

  return {
    state,
    finalColor,
    finalVariant,
    finalSize,
    classes,
  };
};
